<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "$lib/components/ui/card"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import { Label } from "$lib/components/ui/label"
  import { Textarea } from "$lib/components/ui/textarea"
  import type { _response } from "$lib/data/type/_response"
  import { appStore } from "$lib/stores/app-store.svelte"
  import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import MailIcon from "@lucide/svelte/icons/mail"
  import MapPinIcon from "@lucide/svelte/icons/map-pin"
  import PhoneIcon from "@lucide/svelte/icons/phone"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { z } from "zod/v4"

  type Contact = {
    name: string
    phone: string
    email: string
    address: string
  }

  const FormSchema = z.object({
    username: z.string().default(""),
    name: z.string().default(""),
    email: z.string().default(""),
    message: z.string().default(""),
  })
  type FormData = z.infer<typeof FormSchema>
  const FormEmpty = FormSchema.parse({
    username: appStore.user?.username ?? "",
    name: appStore.user?.name ?? "",
    email: appStore.user?.email ?? "",
  })

  const prefix = $derived(page.params.prefix ?? "0")

  const form = createForm(() => ({
    defaultValues: FormEmpty,
  }))

  const query = createQuery(() => ({
    queryKey: ["office", prefix],
    queryFn: async () => {
      const response = await httpClient.get<_response<Contact>>(`/api/${prefix}/info/office`)
      return response.data.result!
    }
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormData) => {

      const response = await httpClient.post<_response<null>>(`/api/${prefix}/info/contact`, data)
      const responseData = response.data

      form.reset(data)
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (previous) => ({
          ...previous,
          errorMap: { onSubmit: { message: messages[0] } },
        }))
      })

      if (response.status !== 201) return

      toast.success(responseData.message)
      form.setFieldValue("message", "")
    },
    onError: (error: Error) => {
      toast.error("Terjadi kesalahan", {
        description: error.message,
      })
    },
  }))

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/setting`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full"
          aria-label="Kembali"
        >
          <ArrowLeftIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Kontak</h1>
      </div>
    </div>
  </nav>
  <div class="space-y-3">
    <div></div>
    <Card class="mx-3 shadow-none">
      <CardHeader class="hidden">
        <CardTitle class="text-base font-bold">Marketing Officer</CardTitle>
      </CardHeader>
      <CardContent class="space-y-3">
        {#if query.isFetching}
          <div class="text-sm">Memuat...</div>
        {:else}
          <div>
            <Label>Marketing Officer</Label>
          </div>
          <div class="flex gap-3">
            <MapPinIcon class="size-5 flex-none" aria-hidden="true"/>
            <div class="text-sm leading-5">
              <div class="font-semibold">{query.data?.name || ""}</div>
              <div>{query.data?.address || "-"}</div>
            </div>
          </div>
          <div class="flex gap-3">
            <PhoneIcon class="size-5 flex-none" aria-hidden="true"/>
            <div class="text-sm leading-5 tabular-nums tracking-tight">
              {query.data?.phone || "-"}
            </div>
          </div>
          <div class="flex gap-3">
            <MailIcon class="size-5 flex-none" aria-hidden="true"/>
            <div class="text-sm leading-5">{query.data?.email || "-"}</div>
          </div>
        {/if}
      </CardContent>
    </Card>
    <form
      action="#"
      autocomplete="off"
      class="mx-3"
      onsubmit={(event) => {
        event.preventDefault()
        mutation.mutate(form.state.values)
      }}
    >
      <Card class="shadow-none">
        <CardHeader>
          <CardTitle class="text-base font-bold">Kirim Pesan</CardTitle>
          <CardDescription>Isi form berikut untuk menghubungi kami.</CardDescription>
        </CardHeader>
        <CardContent class="grid gap-4">
          <form.Field name="name">
            {#snippet children(field)}
              <div>
                <FieldLabel>Nama</FieldLabel>
                <Input
                  type="text"
                  name={field.name}
                  value={field.state.value}
                  readonly
                />
                <FieldMessage name={field.name} {form}/>
              </div>
            {/snippet}
          </form.Field>
          <form.Field name="email">
            {#snippet children(field)}
              <div>
                <FieldLabel>Email</FieldLabel>
                <Input
                  type="email"
                  name={field.name}
                  value={field.state.value}
                  required
                  oninput={(event) => field.handleChange(event.currentTarget.value)}
                />
                <FieldMessage name={field.name} {form}/>
              </div>
            {/snippet}
          </form.Field>
          <form.Field name="message">
            {#snippet children(field)}
              <div>
                <FieldLabel>Pesan</FieldLabel>
                <Textarea
                  name={field.name}
                  value={field.state.value}
                  required
                  oninput={(event) => field.handleChange(event.currentTarget.value)}
                />
                <FieldMessage name={field.name} {form}/>
              </div>
            {/snippet}
          </form.Field>
        </CardContent>
        <CardFooter class="flex-col gap-2">
          <Button
            type="submit"
            class="w-full group"
            data-loading={mutation.isPending}
            disabled={mutation.isPending}
          >
            <span class="group-data-[loading=true]:invisible">Kirim</span>
            <LoaderCircleIcon class="group-data-[loading=true]:visible invisible absolute animate-spin" aria-hidden="true"/>
          </Button>
        </CardFooter>
      </Card>
    </form>
    <div></div>
  </div>
</main>
